import React from "react";
import {
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  IconButton,
  iconButtonVariants,
} from "@racoon/ui";
import { ConnectProto } from "@racoon/api";
import { GripVertical, Ellipsis } from "lucide-react";
import { PhotoReportModal } from "~/features/photo-report-modal/ui/photo-report.modal";
import { Namespace, TranslationKey, useT } from "@racoon/i18n";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { badge } from "~/shared/helpers";
import { plannedContainerCount } from "~/shared/lib/job-containers";

/**
 * Custom badge renderer based on the provided tone (status/type)
 */

/**
 * Status pin icon
 */
const StatusPinIcon: React.FC<{ status: ConnectProto.JobStatus; number: number }> = ({
  status,
  number,
}) => {
  let pinColor = "#D6E6FF";
  let textColorClass = "text-[#0066FF]";

  switch (status) {
    case ConnectProto.JobStatus.ENROUTE:
      pinColor = "#007AFF";
      textColorClass = "text-white";
      break;
    case ConnectProto.JobStatus.ON_SERVICE:
      pinColor = "#FFCC00";
      textColorClass = "text-gray-900";
      break;
    case ConnectProto.JobStatus.COMPLETED:
      pinColor = "#849AB2";
      textColorClass = "text-white";
      break;
    case ConnectProto.JobStatus.CANCELLED:
      pinColor = "#FFC2BE";
      textColorClass = "text-[#FF3B30]";
      break;
  }

  return (
    <div className="relative flex items-center justify-center w-[24px] h-[30px] shrink-0">
      <svg
        viewBox="0 0 20 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="absolute inset-0 w-full h-full drop-shadow-sm"
      >
        <path
          d="M10 0C4.47727 0 0 4.37891 0 9.78031C0 16.2122 8.50182 19.2912 7.31636 20.0079C8.14818 20.7396 8.83545 22.38 9.11273 23.31C9.24727 23.7617 9.62545 23.9902 10 24C10.3755 23.9893 10.7527 23.7608 10.8873 23.31C11.1645 22.38 11.8518 20.7405 12.6836 20.0079C13.4982 19.2912 20 16.2122 20 9.78031C20 4.37891 15.5227 0 10 0Z"
          fill={pinColor}
        />
      </svg>
      <span
        className={cn(
          "relative z-10 top-[-2px] text-[13px] font-bold leading-none tracking-tight",
          textColorClass,
        )}
      >
        {number}
      </span>
    </div>
  );
};

interface RouteJobCardProps {
  job: ConnectProto.Job;
  /** The route's jobs, for stepping through photo reports. */
  jobs?: ConnectProto.Job[];
  index: number;
  onDetach: (jobId: bigint) => void;
  onEdit?: (job: ConnectProto.Job) => void;
  isSaving: boolean;
  isOverlay?: boolean;
  isSelected?: boolean;
  onSelect?: () => void;
  onTransfer?: (jobId: bigint) => void;
  onCancel?: (jobId: bigint) => void;
  isCanceling?: boolean;
}

export const RouteJobCard: React.FC<RouteJobCardProps> = ({
  job,
  jobs,
  index,
  onDetach,
  onEdit,
  isSaving,
  isOverlay = false,
  isSelected = false,
  onSelect,
  onTransfer,
  onCancel,
  isCanceling = false,
}) => {
  const t = useT();

  const isDone = job.status === ConnectProto.JobStatus.COMPLETED;
  const isCancelled = job.status === ConnectProto.JobStatus.CANCELLED;
  const isFinal =
    isDone ||
    isCancelled ||
    job.status === ConnectProto.JobStatus.FORCE_COMPLETED ||
    job.status === ConnectProto.JobStatus.NOT_COMPLETED;
  const isDetachable = !isFinal;
  const isDraggable = job.status === ConnectProto.JobStatus.ASSIGNED;
  const statusStr =
    `JOB_STATUS_${ConnectProto.JobStatus[job.status]}` as TranslationKey<Namespace.Enum>;

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: String(job.id),
    disabled: !isDraggable,
  });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging && !isOverlay ? 0 : 1,
    zIndex: isDragging ? 0 : 1,
  };

  const content = (
    <div
      onClick={onSelect}
      className={cn(
        "group relative flex-1 min-w-0 overflow-hidden p-4 transition-all duration-200 cursor-pointer rounded-[12px] border",
        isSelected ? "bg-white border-[#0066FF] shadow-sm" : "bg-white ",
        !isDraggable && "opacity-95",
        isOverlay
          ? "bg-white shadow-2xl border-[#0066FF] scale-[1.02] ring-4 ring-blue-500/10"
          : "",
        isDragging && !isOverlay ? "border-dashed border-gray-300 bg-gray-50/50 opacity-50" : "",
      )}
    >
      <div className="flex justify-between items-start mb-3 gap-2">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <StatusPinIcon number={index + 1} status={job.status} />
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline gap-2 min-w-0">
              <h4
                title={job.pickupLocation?.name || undefined}
                className="font-bold text-gray-900 text-[16px] underline decoration-dotted underline-offset-4 decoration-gray-400 truncate"
              >
                {job.pickupLocation?.name || "Точка"}
              </h4>
            </div>
            <div className="flex gap-2 text-xs text-text-secondary font-medium min-w-0">
              <span className="shrink-0 whitespace-nowrap"> Z{Number(job.id)}</span>
              <span className="shrink-0 whitespace-nowrap">
                {job.type !== undefined
                  ? t(Namespace.Orders, `types.${ConnectProto.JobType[job.type]}` as any)
                  : ""}
              </span>
              <span className="min-w-0 truncate" title={job.serviceReceiver?.name || undefined}>
                {job.serviceReceiver?.name || ""}
              </span>
            </div>
          </div>
        </div>

        {/* Using the custom badge instead of the standard one */}
        <div className="flex items-center gap-2 shrink-0">
          {badge(t(Namespace.Enum, statusStr), statusStr)}
          <div className="flex items-end pb-1" onClick={(e) => e.stopPropagation()}>
            <PhotoReportModal
              job={job}
              getJobs={jobs && (() => jobs)}
              triggerClassName={iconButtonVariants({ size: "lg" })}
            />
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <IconButton size="md">
                <Ellipsis />
              </IconButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem
                disabled={isSaving}
                onClick={(e) => {
                  if (isSaving) return;
                  e.stopPropagation();
                  onEdit?.(job);
                }}
              >
                {t(Namespace.Routes, "editJob")}
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={isSaving || !isDetachable}
                onClick={(e) => {
                  if (isSaving || !isDetachable) return;
                  e.stopPropagation();
                  onDetach(job.id);
                }}
              >
                {t(Namespace.Routes, "removeJobFromRoute")}
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={isSaving || !isDetachable}
                onClick={(e) => {
                  if (isSaving || !isDetachable) return;
                  e.stopPropagation();
                  onTransfer?.(job.id);
                }}
              >
                {t(Namespace.Routes, "transferJob")}
              </DropdownMenuItem>
              {isDetachable && (
                <DropdownMenuItem
                  disabled={isSaving || isCanceling}
                  className="text-[#FF3B30] focus:text-[#FF3B30] focus:bg-[#FFF1F0]"
                  onClick={(e) => {
                    if (isSaving || isCanceling) return;
                    e.stopPropagation();
                    onCancel?.(job.id);
                  }}
                >
                  {isCanceling
                    ? t(Namespace.Routes, "canceling")
                    : t(Namespace.Routes, "cancelOrder")}
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="flex items-center gap-2 min-w-0">
        <div className="shrink-0">
          <div
            {...(!isOverlay && isDraggable ? attributes : {})}
            {...(!isOverlay && isDraggable ? listeners : {})}
            className={cn(
              "text-gray-400 hover:text-gray-600 p-1 -ml-1 transition-colors",
              isDraggable ? "cursor-grab active:cursor-grabbing" : "cursor-default opacity-40",
            )}
          >
            <GripVertical size={20} />
          </div>
        </div>
        <div className="space-y-1.5 text-[14px] text-gray-800 min-w-0 flex-1">
          <div className="flex min-w-0">
            <span className="text-gray-500 mr-1 shrink-0 whitespace-nowrap">
              {t(Namespace.Customers, "card.objects.column.containerGroup")}:
            </span>
            <span
              className="font-medium min-w-0 truncate"
              title={
                job.containerGroup?.containerType?.name
                  ? `${job.containerGroup.containerType.name}${
                      plannedContainerCount(job) ? ` (${plannedContainerCount(job)})` : ""
                    }`
                  : undefined
              }
            >
              {job.containerGroup?.containerType?.name || ""}{" "}
              {plannedContainerCount(job) ? `(${plannedContainerCount(job)})` : ""}
            </span>
          </div>
          <div className="flex min-w-0">
            <span className="text-gray-500 mr-1 shrink-0 whitespace-nowrap">
              {t(Namespace.Jobs, "comment")}:
            </span>
            <span className="font-medium min-w-0 truncate" title={job.driverComment || undefined}>
              {job.driverComment || "---"}
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  if (isOverlay)
    return <div className="relative flex w-full min-w-0 pointer-events-none">{content}</div>;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "relative flex w-full min-w-0 group",
        isOverlay && "z-[9999] pointer-events-none",
      )}
    >
      {content}
    </div>
  );
};
